import { useState, useEffect, useMemo } from "react";
import {
  RevDialog,
  RevButton,
  RevSpinner,
  RevCheckbox,
  RevAvatar,
} from "@revmatics/catalyst";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight } from "@fortawesome/pro-light-svg-icons";
import { useAppDispatch, useAppSelector } from "../../../../stores/hooks/hooks";
import { assignPersonasToPersonaGroup } from "../../../../stores/thunks/personaGroupsThunk";
import { fetchPersonasData } from "../../../../stores/thunks/personasThunk";
import { personaGroupType } from "../../../../stores/types/personaGroupsTypes";
import { PersonaAPIResponseData } from "../../../../stores/types/personasTypes";
import { PersonaDetailView } from "../../../../components/layout/components/globalSlideouts/components/persona/PersonaDetailView.tsx";

type Props = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  group: personaGroupType;
  onSuccess: () => void;
};

export const PersonaSelectorDialog: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  group,
  onSuccess,
}) => {
  const dispatch = useAppDispatch();

  const allPersonas = useAppSelector(
    (state) => state.personas.personas,
  ) as PersonaAPIResponseData[];

  const isLoading = useAppSelector((state) => state.personas.isLoadingList);
  const isAssigning = useAppSelector(
    (state) => state.personaGroups.isAssigning,
  );

  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  useEffect(() => {
    if (isOpen) dispatch(fetchPersonasData());
  }, [isOpen, dispatch]);

  const filteredPersonas = useMemo(() => {
    if (!allPersonas) return [];
    return allPersonas.filter((p) => !!p.is_platform === !!group.is_platform);
  }, [allPersonas, group.is_platform]);

  const handleToggleSelect = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  };

  const handleClose = () => {
    setSelectedIds([]);
    setIsOpen(false);
  };

  const handleSubmit = async () => {
    if (selectedIds.length === 0) return;
    try {
      await dispatch(
        assignPersonasToPersonaGroup({
          id: group.id,
          body: { persona_ids: selectedIds },
        }),
      ).unwrap();
      onSuccess();
      handleClose();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <RevDialog
      isOpen={isOpen}
      setIsOpen={handleClose}
      title={`Add Personas to ${group.name}`}
      size="s"
      footerComponent={
        <div className="flex justify-end items-center px-6 py-4 border-t border-monochrome-100 bg-white shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]">
          <div className="flex gap-3">
            <RevButton
              text="Cancel"
              variant="secondary"
              handleClick={handleClose}
            />
            <RevButton
              text={`Add Persona${selectedIds.length !== 1 ? "s" : ""} to Generation`}
              disabled={selectedIds.length === 0 || isAssigning}
              isLoading={isAssigning}
              handleClick={handleSubmit}
            />
          </div>
        </div>
      }
    >
      <div className="flex flex-col w-full">
        <div className="py-4 flex justify-between items-end">
          <div>
            <h6 className="typography-body-bold-sm text-monochrome-900">
              Personas
            </h6>
            <p className="typography-body-xs text-monochrome-500">
              {filteredPersonas.length}{" "}
              {filteredPersonas.length === 1 ? "persona" : "personas"}
            </p>
          </div>
          {selectedIds.length > 0 && (
            <span className="typography-body-xs text-monochrome-600 animate-in fade-in slide-in-from-right-2">
              {selectedIds.length}{" "}
              {selectedIds.length === 1 ? "persona" : "personas"} selected
            </span>
          )}
        </div>

        <div className="flex-1 pb-6 overflow-y-auto max-h-[350px] w-full">
          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <RevSpinner size="medium" color="primary" />
            </div>
          ) : filteredPersonas.length > 0 ? (
            <div className="space-y-3 w-full bg-primary-25 border border-monochrome-200 p-4 rounded-md ">
              {filteredPersonas.map((persona) => {
                const isSelected = selectedIds.includes(persona.id);
                const imageUrl =
                  persona.persona_picture_url || persona.persona_picture;

                return (
                  <Disclosure key={persona.id}>
                    {({ open }) => (
                      <div
                        className={`rounded-md shadow-sm transition-all duration-200 bg-white w-full ${
                          isSelected
                            ? "outline-2 outline-primary-500"
                            : "outline-1 outline-monochrome-200 hover:outline-primary-500"
                        }`}
                      >
                        <div className="flex items-center p-2 gap-3 w-full">
                          <RevCheckbox
                            checked={isSelected}
                            handleChecked={() => handleToggleSelect(persona.id)}
                          />
                          <DisclosureButton className="flex flex-1 items-center justify-between text-left focus:outline-none">
                            <div className="flex items-center gap-3">
                              {imageUrl ? (
                                <div className="h-10 w-10 border border-monochrome-100 rounded-full overflow-hidden flex-shrink-0">
                                  <img
                                    src={imageUrl}
                                    alt={persona.name}
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                              ) : (
                                <RevAvatar
                                  variant="circle"
                                  size="medium"
                                  theme="light"
                                  firstName={persona.name.split(" ")[0]}
                                  gender={persona.gender}
                                />
                              )}
                              <p className="font-medium text-monochrome-900">
                                {persona.name}
                              </p>
                            </div>
                            <FontAwesomeIcon
                              icon={faChevronRight}
                              className={`text-monochrome-400 transition-transform duration-200 ${
                                open ? "rotate-90" : ""
                              }`}
                            />
                          </DisclosureButton>
                        </div>

                        <DisclosurePanel className="px-12 pb-6 border-t border-monochrome-50 pt-4 bg-monochrome-25 rounded-b-md w-full">
                          <PersonaDetailView persona={persona} />
                        </DisclosurePanel>
                      </div>
                    )}
                  </Disclosure>
                );
              })}
            </div>
          ) : (
            <div className="py-12 text-center border-2 border-dashed border-monochrome-100 rounded-lg bg-white w-full">
              <p className="typography-body-sm text-monochrome-500">
                No compatible personas found.
              </p>
            </div>
          )}
        </div>
      </div>
    </RevDialog>
  );
};
